import React, { useMemo, useState } from "react";
import {
  Check, ChevronLeft, ChevronRight, Copy, Download, Inbox, Layers,
  Plus, Target, Trash2, X,
} from "lucide-react";
import { CHECK, CHECK_IDS } from "../../checks";
import { EVAL_FLAGS } from "../../constants";
import { cloneJSON, fmtDate, guessDocType, nowStr, uid } from "../../utils";
import { corpusExportUrl } from "../../api/forecast";
import {
  applyAttachmentServerIds, applySyncResult, mirrorCorpusDelete, mirrorCorpusRename,
  mirrorEmailCreate, mirrorEmailDelete, mirrorEmailUpdate, syncCorpusToServer,
} from "../../services/matterSync";
import { WorkstreamCard, type WorkstreamCardHandlers } from "./WorkstreamCard";
import { CorpusEstimateBar } from "./CorpusEstimateBar";
import { SettingsView } from "./SettingsView";
import { Workbench } from "../../workbench/Workbench";
import { fmtUsd } from "../../services/costEstimate";
import { EmailEditor, type EmailEditorState } from "../Import/EmailEditor";
import { NewEntityModal } from "../Import/NewEntityModal";
import type { ImportItemRecord } from "../Import/ImportItem";
import type { ImportTargetType } from "../../importPipeline";
import type {
  CorpusSet, EvalSet, SimEmail,
  WorkstreamBundle, WorkstreamLite,
} from "../../types";

export interface LibraryProps {
  tab: string;
  setTab: (id: string) => void;
  isMobile: boolean;
  corpuses: CorpusSet[];
  setCorpuses: React.Dispatch<React.SetStateAction<CorpusSet[]>>;
  evalSets: EvalSet[];
  setEvalSets: React.Dispatch<React.SetStateAction<EvalSet[]>>;
  wsBundles: WorkstreamBundle[];
  setWsBundles: React.Dispatch<React.SetStateAction<WorkstreamBundle[]>>;
}

type NewModalState = { type: ImportTargetType } | null;

/* Library page — composes the four asset tabs (corpus / eval / checks / ws)
   into a single editor surface, all state changes route through the setters
   that ultimately persist on the server. */
export function LibraryView(props: LibraryProps): JSX.Element {
  const {
    tab, setTab, isMobile, corpuses, setCorpuses, evalSets, setEvalSets,
    wsBundles, setWsBundles,
  } = props;
  const [openCorpus, setOpenCorpus] = useState<string | null>(null);
  const [editing, setEditing] = useState<EmailEditorState | null>(null);
  const [openEval, setOpenEval] = useState<string | null>(null);
  const [openBundle, setOpenBundle] = useState<string | null>(null);
  const [newModal, setNewModal] = useState<NewModalState>(null);
  const [confirmDel, setConfirmDel] = useState<CorpusSet | null>(null);
  /* While a bundle is open in the Agent Registry, the workbench brings its
     own header band — the Library page heading gets out of the way. */
  const [wbOpen, setWbOpen] = useState(false);
  const slimChrome = wbOpen && tab === "checks";
  const tabs: Array<[string, string]> = [
    ["corpus", "Email Corpus"],
    ["eval", "Eval Sets"],
    ["checks", "Agent Registry"],
    ["ws", "Workstreams"],
    ["settings", "Settings"],
  ];
  const allWs: WorkstreamLite[] = (() => {
    const seen: Record<string, number> = {};
    const out: WorkstreamLite[] = [];
    wsBundles.forEach((b) =>
      b.workstreams.forEach((w) => {
        if (!seen[w.id]) {
          seen[w.id] = 1;
          out.push(w);
        }
      }),
    );
    return out;
  })();

  /* corpus ops — local state first, then mirrored to the relational API */
  const syncNewCorpus = (fresh: CorpusSet): void => {
    void syncCorpusToServer(fresh).then((r) => {
      if (r)
        setCorpuses((cs) => cs.map((c) => (c.id === fresh.id ? applySyncResult(c, r) : c)));
    });
  };
  const newCorpus = (): void => {
    const fresh: CorpusSet = {
      id: "corp_" + uid(),
      name: "Untitled corpus",
      note: "New collection",
      updated: nowStr(),
      emails: [],
      log: ["Created"],
    };
    setCorpuses((cs) => [...cs, fresh]);
    setOpenCorpus(fresh.id);
    syncNewCorpus(fresh);
  };
  const dupCorpus = (c: CorpusSet): void => {
    const fresh: CorpusSet = {
      id: "corp_" + uid(),
      name: c.name + " (variant)",
      note: c.note,
      updated: nowStr(),
      emails: (cloneJSON(c.emails) as SimEmail[]).map((e) => ({ ...e, serverId: undefined })),
      log: ["Duplicated from " + c.name],
    };
    setCorpuses((cs) => [...cs, fresh]);
    setOpenCorpus(fresh.id);
    syncNewCorpus(fresh);
  };
  const delCorpus = (id: string): void => {
    const victim = corpuses.find((c) => c.id === id);
    if (corpuses.length > 1 && victim) void mirrorCorpusDelete(victim);
    setCorpuses((cs) => (cs.length > 1 ? cs.filter((c) => c.id !== id) : cs));
    setOpenCorpus(null);
  };
  const renameCorpus = (id: string, name: string): void => {
    const c = corpuses.find((x) => x.id === id);
    if (c?.matterId) mirrorCorpusRename(c.matterId, name);
    setCorpuses((cs) => cs.map((x) => (x.id === id ? { ...x, name } : x)));
  };
  const saveEmail = (corpusId: string, email: SimEmail, isNew: boolean): void => {
    const c0 = corpuses.find((c) => c.id === corpusId);
    const nid = isNew
      ? ((c0 ? c0.emails.reduce((m, e) => Math.max(m, e.id), 0) : 0) || 0) + 1
      : email.id;
    const saved: SimEmail = isNew ? { ...email, id: nid } : email;
    setCorpuses((cs) =>
      cs.map((c) => {
        if (c.id !== corpusId) return c;
        const emails = isNew
          ? [...c.emails, saved]
          : c.emails.map((e) => (e.id === email.id ? { ...e, ...email } : e));
        return {
          ...c,
          emails,
          updated: nowStr(),
          log: [...(c.log || []), isNew ? "Added email" : "Edited email #" + email.id],
        };
      }),
    );
    if (c0?.matterId) {
      if (isNew) {
        void mirrorEmailCreate(c0.matterId, saved).then((mirrored) => {
          if (!mirrored) return;
          setCorpuses((cs) =>
            cs.map((c) =>
              c.id === corpusId
                ? {
                    ...c,
                    emails: c.emails.map((e) =>
                      e.id === nid
                        ? {
                            ...e,
                            serverId: mirrored.id,
                            attachments: applyAttachmentServerIds(
                              e.attachments,
                              mirrored.attachments,
                            ),
                          }
                        : e,
                    ),
                  }
                : c,
            ),
          );
        });
      } else {
        const prev = c0.emails.find((e) => e.id === email.id);
        void mirrorEmailUpdate({ ...email, serverId: email.serverId ?? prev?.serverId }).then(
          (refs) => {
            if (!refs) return;
            setCorpuses((cs) =>
              cs.map((c) =>
                c.id === corpusId
                  ? {
                      ...c,
                      emails: c.emails.map((e) =>
                        e.id === email.id
                          ? {
                              ...e,
                              attachments: applyAttachmentServerIds(
                                (e.attachments || []).map((a) => ({
                                  ...a,
                                  serverId: undefined,
                                })),
                                refs,
                              ),
                            }
                          : e,
                      ),
                    }
                  : c,
              ),
            );
          },
        );
      }
    }
    setEditing(null);
  };
  const delEmail = (corpusId: string, emailId: number): void => {
    const victim = corpuses
      .find((c) => c.id === corpusId)
      ?.emails.find((e) => e.id === emailId);
    if (victim) void mirrorEmailDelete(victim);
    setCorpuses((cs) =>
      cs.map((c) =>
        c.id === corpusId
          ? {
              ...c,
              emails: c.emails.filter((e) => e.id !== emailId),
              updated: nowStr(),
              log: [...(c.log || []), "Deleted email #" + emailId],
            }
          : c,
      ),
    );
    setEditing(null);
  };

  /* eval ops */
  const newEval = (corpusId: string): void => {
    const id = "eval_" + uid();
    setEvalSets((es) => [
      ...es,
      { id, name: "New eval set", corpusId, when: nowStr(), expected: {} },
    ]);
    setOpenEval(id);
  };
  const dupEval = (ev: EvalSet): void => {
    const id = "eval_" + uid();
    setEvalSets((es) => [
      ...es,
      { ...(cloneJSON(ev) as EvalSet), id, name: ev.name + " (copy)", when: nowStr() },
    ]);
    setOpenEval(id);
  };
  const delEval = (id: string): void => {
    setEvalSets((es) => es.filter((e) => e.id !== id));
    setOpenEval(null);
  };
  const renameEval = (id: string, name: string): void =>
    setEvalSets((es) => es.map((e) => (e.id === id ? { ...e, name } : e)));
  const setEvalCorpus = (id: string, corpusId: string): void =>
    setEvalSets((es) => es.map((e) => (e.id === id ? { ...e, corpusId } : e)));
  const setExpected = (
    evalId: string,
    emailId: number,
    patch: Record<string, unknown>,
  ): void =>
    setEvalSets((es) =>
      es.map((e) => {
        if (e.id !== evalId) return e;
        const cur = e.expected[emailId] || { catalog: ["unassoc"] };
        return { ...e, expected: { ...e.expected, [emailId]: { ...cur, ...patch } } };
      }),
    );
  const setFinalTrackers = (evalId: string, value: EvalSet["finalTrackers"]): void =>
    setEvalSets((es) => es.map((e) => (e.id === evalId ? { ...e, finalTrackers: value } : e)));

  /* JSON answer-key textareas: local raw text until it parses, then commit. */
  const [jsonDrafts, setJsonDrafts] = useState<Record<string, string>>({});
  const [jsonBad, setJsonBad] = useState<Record<string, boolean>>({});
  const editJson = (key: string, raw: string, commit: (parsed: unknown) => void): void => {
    setJsonDrafts((d) => ({ ...d, [key]: raw }));
    if (!raw.trim()) {
      setJsonBad((b) => ({ ...b, [key]: false }));
      commit(undefined);
      return;
    }
    try {
      const v = JSON.parse(raw);
      setJsonBad((b) => ({ ...b, [key]: false }));
      commit(v);
    } catch {
      setJsonBad((b) => ({ ...b, [key]: true }));
    }
  };
  const contractHint = useMemo(
    () =>
      "Expected outputs per check (declared contracts):\n" +
      CHECK_IDS.filter((id) => (CHECK[id]?.outputs || []).length)
        .map((id) => `${id}: ${CHECK[id].outputs!.join(", ")}`)
        .join("\n"),
    [],
  );

  /* workstream bundle ops */
  const newBundle = (): void => {
    const id = "wsb_" + uid();
    setWsBundles((bs) => [
      ...bs,
      {
        id,
        name: "Untitled bundle",
        note: "New workstream bundle",
        createdAt: nowStr(),
        workstreams: [],
        log: ["Created"],
      },
    ]);
    setOpenBundle(id);
  };
  const dupBundle = (b: WorkstreamBundle): void => {
    const id = "wsb_" + uid();
    setWsBundles((bs) => [
      ...bs,
      {
        id,
        name: b.name + " (variant)",
        note: b.note,
        createdAt: nowStr(),
        workstreams: cloneJSON(b.workstreams) as WorkstreamLite[],
        log: ["Duplicated from " + b.name],
      },
    ]);
    setOpenBundle(id);
  };
  const delBundle = (id: string): void => {
    setWsBundles((bs) => (bs.length > 1 ? bs.filter((b) => b.id !== id) : bs));
    setOpenBundle(null);
  };
  const renameBundle = (id: string, name: string): void =>
    setWsBundles((bs) => bs.map((b) => (b.id === id ? { ...b, name } : b)));
  const patchBundleWs = (
    bid: string,
    fn: (arr: WorkstreamLite[]) => WorkstreamLite[],
    note?: string,
  ): void =>
    setWsBundles((bs) =>
      bs.map((b) =>
        b.id === bid
          ? {
              ...b,
              workstreams: fn(b.workstreams),
              log: note ? [...(b.log || []), note] : b.log,
            }
          : b,
      ),
    );
  const addWorkstream = (bid: string): void =>
    patchBundleWs(
      bid,
      (arr) => [
        ...arr,
        {
          id: "ws_" + uid(),
          name: "New workstream",
          code: "M-" + Math.floor(1000 + Math.random() * 9000),
          type: "Matter",
          phase: "Setup",
          dealType: "",
          entities: [],
          memoFields: [],
          capTable: { attached: false, name: "" },
        } as WorkstreamLite,
      ],
      "Added workstream",
    );
  const rmWorkstream = (bid: string, wsId: string): void =>
    patchBundleWs(bid, (arr) => arr.filter((w) => w.id !== wsId), "Removed workstream");
  const editWs = (bid: string, idx: number, patch: Partial<WorkstreamLite>): void =>
    patchBundleWs(bid, (arr) => arr.map((w, i) => (i === idx ? { ...w, ...patch } : w)));
  const patchWs = (
    bid: string,
    idx: number,
    fn: (w: WorkstreamLite) => WorkstreamLite,
  ): void => patchBundleWs(bid, (arr) => arr.map((w, i) => (i === idx ? fn(w) : w)));
  const addEntity = (bid: string, idx: number): void =>
    patchWs(bid, idx, (w) => ({
      ...w,
      entities: [
        ...w.entities,
        { id: "e_" + uid(), name: "New entity", kind: "Investor", internal: false, people: [] },
      ],
    }));
  const rmEntity = (bid: string, idx: number, eid: string): void =>
    patchWs(bid, idx, (w) => ({ ...w, entities: w.entities.filter((e) => e.id !== eid) }));
  const editEntity = (
    bid: string,
    idx: number,
    eid: string,
    patch: Record<string, unknown>,
  ): void =>
    patchWs(bid, idx, (w) => ({
      ...w,
      entities: w.entities.map((e) => (e.id === eid ? ({ ...e, ...patch } as typeof e) : e)),
    }));
  const addPerson = (bid: string, idx: number, eid: string): void =>
    patchWs(bid, idx, (w) => ({
      ...w,
      entities: w.entities.map((e) =>
        e.id === eid ? { ...e, people: [...e.people, { name: "", email: "", role: "" }] } : e,
      ),
    }));
  const rmPerson = (bid: string, idx: number, eid: string, pi: number): void =>
    patchWs(bid, idx, (w) => ({
      ...w,
      entities: w.entities.map((e) =>
        e.id === eid ? { ...e, people: e.people.filter((_, j) => j !== pi) } : e,
      ),
    }));
  const editPerson = (
    bid: string,
    idx: number,
    eid: string,
    pi: number,
    col: string,
    val: string,
  ): void =>
    patchWs(bid, idx, (w) => ({
      ...w,
      entities: w.entities.map((e) =>
        e.id === eid
          ? {
              ...e,
              people: e.people.map((pp, j) =>
                j === pi ? ({ ...pp, [col]: val } as typeof pp) : pp,
              ),
            }
          : e,
      ),
    }));
  const addMemoField = (bid: string, idx: number): void =>
    patchWs(bid, idx, (w) => ({ ...w, memoFields: [...w.memoFields, { k: "", v: "" }] }));
  const rmMemoField = (bid: string, idx: number, fi: number): void =>
    patchWs(bid, idx, (w) => ({
      ...w,
      memoFields: w.memoFields.filter((_, j) => j !== fi),
    }));
  const editMemoField = (
    bid: string,
    idx: number,
    fi: number,
    col: string,
    val: string,
  ): void =>
    patchWs(bid, idx, (w) => ({
      ...w,
      memoFields: w.memoFields.map((f, j) =>
        j === fi ? ({ ...f, [col]: val } as typeof f) : f,
      ),
    }));
  const toggleCap = (bid: string, idx: number): void =>
    patchWs(bid, idx, (w) => ({
      ...w,
      capTable: {
        ...(w.capTable || { name: "" }),
        attached: !(w.capTable && w.capTable.attached),
      } as { attached: boolean; name: string },
    }));
  const setCapName = (bid: string, idx: number, name: string): void =>
    patchWs(bid, idx, (w) => ({
      ...w,
      capTable: { ...(w.capTable || { attached: true }), name } as {
        attached: boolean;
        name: string;
      },
    }));
  const uploadDocument = (
    bid: string,
    idx: number,
    u: { filename: string; fileData?: string },
  ): void =>
    patchWs(bid, idx, (w) => {
      const doc = {
        id: "doc_" + uid(),
        name: String(u.filename || "Document")
          .replace(/\.[A-Za-z0-9]+$/, "")
          .replace(/_/g, " "),
        doc_type: guessDocType(u.filename),
        current_version: 1,
        status: "draft",
        bears_on: [],
        versions: [
          {
            version: 1,
            date: nowStr(),
            filename: u.filename,
            fileData: u.fileData,
            uploaded: true,
          },
        ],
      };
      return { ...w, documents: [...(w.documents || []), doc] };
    });
  const wsOps: WorkstreamCardHandlers = {
    editWs, rmWorkstream, addEntity, rmEntity, editEntity, addPerson, rmPerson,
    editPerson, addMemoField, rmMemoField, editMemoField, toggleCap, setCapName,
    uploadDocument,
  };

  /* intelligent import: create entities from processed files */
  const importEntities = (items: ImportItemRecord[]): void => {
    let firstCorpus: string | null = null;
    let firstEval: string | null = null;
    let firstBundle: string | null = null;
    items
      .filter(
        (it) => it.include && it.status !== "error" && it.status !== "processing" && it.payload,
      )
      .forEach((it) => {
        if (it.asType === "corpus") {
          const id = "corp_" + uid();
          const emails = (it.payload.emails || []).map((e: SimEmail, i: number) => ({
            ...e,
            id: i + 1,
            to: e.to || [],
            cc: e.cc || [],
            attachments: e.attachments || [],
          }));
          const fresh: CorpusSet = {
            id,
            name: it.name || "Imported corpus",
            note: "Imported · " + emails.length + " email" + (emails.length === 1 ? "" : "s"),
            updated: nowStr(),
            emails,
            log: ["Created via import", emails.length + " emails imported"],
          };
          setCorpuses((cs) => [...cs, fresh]);
          syncNewCorpus(fresh);
          if (!firstCorpus) firstCorpus = id;
        } else if (it.asType === "eval") {
          const id = "eval_" + uid();
          setEvalSets((es) => [
            ...es,
            {
              id,
              name: it.name || it.payload.name,
              corpusId: it.payload.corpusId,
              when: nowStr(),
              expected: it.payload.expected || {},
            },
          ]);
          if (!firstEval) firstEval = id;
        } else if (it.asType === "workstream") {
          const id = "wsb_" + uid();
          setWsBundles((bs) => [
            ...bs,
            {
              id,
              name: it.name || it.payload.name,
              note: it.payload.note || "Imported",
              createdAt: nowStr(),
              workstreams: it.payload.workstreams || [],
              log: [
                "Created via import",
                (it.payload.workstreams || []).length + " workstreams",
              ],
            },
          ]);
          if (!firstBundle) firstBundle = id;
        }
      });
    setNewModal(null);
    if (firstCorpus) {
      setTab("corpus");
      setOpenCorpus(firstCorpus);
    } else if (firstEval) {
      setTab("eval");
      setOpenEval(firstEval);
    } else if (firstBundle) {
      setTab("ws");
      setOpenBundle(firstBundle);
    }
  };
  const startBlank = (): void => {
    const t = newModal && newModal.type;
    setNewModal(null);
    if (t === "corpus") newCorpus();
    else if (t === "eval") newEval((corpuses[0] || ({} as CorpusSet)).id);
    else if (t === "workstream") newBundle();
  };

  const corpus = corpuses.find((c) => c.id === openCorpus);
  const evalSet = evalSets.find((e) => e.id === openEval);
  const evalCorpus = evalSet ? corpuses.find((c) => c.id === evalSet.corpusId) : null;

  return (
    <main className="page" id="main">
      <div className="content scroll">
        {!slimChrome && (
          <div className="pagehd" style={{ padding: "24px 0 16px" }}>
            <div>
              <h1 className="h1">Library</h1>
              <div className="psub">
                Reusable assets assembled into simulation runs · versioned and editable
              </div>
            </div>
          </div>
        )}
        <div style={{ display: "flex", gap: 6, marginBottom: slimChrome ? 0 : 18, flexWrap: "wrap", paddingTop: slimChrome ? 12 : 0 }}>
          {tabs.map(([id, lab]) => (
            <button
              key={id}
              className={"btn btn-sm " + (tab === id ? "btn-primary" : "btn-ghost")}
              onClick={() => {
                setTab(id);
                setOpenCorpus(null);
                setOpenEval(null);
              }}
            >
              {lab}
            </button>
          ))}
        </div>

        {/* CORPUS */}
        {tab === "corpus" && !corpus && (
          <>
            <div className="lbar">
              <span className="up">{corpuses.length} corpuses</span>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setNewModal({ type: "corpus" })}
              >
                <Plus size={14} /> New corpus
              </button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {corpuses.map((c) => {
                const isEmpty = c.emails.length === 0;
                return (
                  <div className="card asset assetrow" key={c.id}>
                    <div
                      className="logo"
                      style={{
                        background: "var(--subtle)",
                        color: "var(--fg)",
                        opacity: isEmpty ? 0.45 : 1,
                      }}
                    >
                      <Inbox size={14} />
                    </div>
                    <div
                      style={{ flex: 1, minWidth: 0, cursor: "pointer" }}
                      onClick={() => setOpenCorpus(c.id)}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontWeight: 600, opacity: isEmpty ? 0.6 : 1 }}>
                          {c.name}
                        </span>
                        {isEmpty && (
                          <span className="tchip" style={{ color: "var(--tert)" }}>
                            Empty
                          </span>
                        )}
                      </div>
                      <div className="mono tert" style={{ fontSize: 11, marginTop: 2 }}>
                        {c.emails.length} email{c.emails.length === 1 ? "" : "s"} · updated{" "}
                        {fmtDate(c.updated)} ·{" "}
                        {c.lastEstimate ? "Est. " + fmtUsd(c.lastEstimate.cost) : "Est. not run"}
                      </div>
                      <div className="tert" style={{ fontSize: 12, marginTop: 2 }}>
                        {c.note}
                      </div>
                    </div>
                    <div className="assetacts">
                      <button className="btn btn-ghost btn-sm" onClick={() => dupCorpus(c)}>
                        <Layers size={13} /> Duplicate
                      </button>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => setOpenCorpus(c.id)}
                      >
                        Open <ChevronRight size={14} />
                      </button>
                      <button
                        className="btn btn-ghost btn-sm"
                        title={
                          corpuses.length <= 1
                            ? "The last corpus can't be deleted"
                            : "Delete corpus"
                        }
                        aria-label={"Delete corpus " + c.name}
                        disabled={corpuses.length <= 1}
                        onClick={() => setConfirmDel(c)}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
        {tab === "corpus" && corpus && (
          <>
            <div className="crumb">
              <button className="btn btn-ghost btn-sm" onClick={() => setOpenCorpus(null)}>
                <ChevronLeft size={14} /> Corpuses
              </button>
              <input
                className="inp"
                style={{ flex: 1, maxWidth: 360 }}
                value={corpus.name}
                onChange={(e) => renameCorpus(corpus.id, e.target.value)}
              />
              <button className="btn btn-ghost btn-sm" onClick={() => dupCorpus(corpus)}>
                <Layers size={14} /> Duplicate
              </button>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setConfirmDel(corpus)}
                disabled={corpuses.length <= 1}
              >
                <X size={14} /> Delete
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={() =>
                  setEditing({
                    corpusId: corpus.id,
                    email: {
                      id: 0,
                      fromName: "",
                      from: "",
                      to: [],
                      cc: [],
                      subject: "",
                      time: nowStr() + " · 9:00 AM",
                      raw: "",
                    } as SimEmail,
                    isNew: true,
                  })
                }
              >
                <Plus size={14} /> Add email
              </button>
              <button
                className="btn btn-ghost btn-sm"
                title={
                  corpus.matterId
                    ? "Download corpus — emails + attachments as a ZIP for external forecast generation"
                    : "Download corpus — unavailable until this corpus is synced to the backend"
                }
                onClick={() => window.open(corpusExportUrl(corpus.matterId!), "_blank")}
                disabled={!corpus.matterId}
              >
                <Download size={14} /> Download
              </button>
            </div>
            <CorpusEstimateBar
              corpus={corpus}
              onEstimated={(corpusId, checkBundleId, est) =>
                setCorpuses((cs) =>
                  cs.map((c) =>
                    c.id === corpusId
                      ? {
                          ...c,
                          lastEstimate: {
                            cost: est.totalCost,
                            checkBundleId,
                            at: nowStr(),
                          },
                        }
                      : c,
                  ),
                )
              }
            />
            {corpus.emails.length === 0 && (
              <div className="card" style={{ padding: 28, textAlign: "center" }}>
                <div className="empty" style={{ padding: 0 }}>
                  No emails yet.
                  <br />
                  Add one, or duplicate another corpus and edit it into a variant.
                </div>
              </div>
            )}
            {corpus.emails.length > 0 &&
              (isMobile ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {corpus.emails.map((e) => (
                    <div
                      className="card"
                      key={e.id}
                      style={{ padding: 14, cursor: "pointer" }}
                      onClick={() =>
                        setEditing({ corpusId: corpus.id, email: e, isNew: false })
                      }
                    >
                      <div style={{ fontWeight: 600, fontSize: 13.5, marginBottom: 4 }}>
                        {e.subject || "(no subject)"}
                      </div>
                      <div className="mono tert" style={{ fontSize: 11 }}>
                        {e.from}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="card" style={{ overflow: "hidden" }}>
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Subject</th>
                        <th>From</th>
                        <th>Recipients</th>
                        <th>Date</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {corpus.emails.map((e) => (
                        <tr
                          key={e.id}
                          className="click"
                          onClick={() =>
                            setEditing({ corpusId: corpus.id, email: e, isNew: false })
                          }
                        >
                          <td className="mono tert">{e.id}</td>
                          <td style={{ fontWeight: 600 }}>{e.subject || "(no subject)"}</td>
                          <td className="mono" style={{ fontSize: 12 }}>
                            {e.from}
                          </td>
                          <td className="mono tert" style={{ fontSize: 11 }}>
                            {(e.to || []).length} To
                            {(e.cc || []).length ? " · " + e.cc.length + " Cc" : ""}
                          </td>
                          <td className="mono muted" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                            {fmtDate((e.time || "").split(" · ")[0])}
                          </td>
                          <td style={{ textAlign: "right" }}>
                            <ChevronRight size={15} color="var(--tert)" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            <div className="up" style={{ margin: "18px 0 8px" }}>
              Change log
            </div>
            <div className="card" style={{ padding: "6px 14px" }}>
              {(corpus.log || [])
                .slice()
                .reverse()
                .map((l, i) => (
                  <div
                    className="kv"
                    key={i}
                    style={{
                      borderBottom:
                        i === (corpus.log || []).length - 1 ? "none" : undefined,
                    }}
                  >
                    <span className="k">{l}</span>
                  </div>
                ))}
            </div>
          </>
        )}

        {/* EVAL SETS */}
        {tab === "eval" && !evalSet && (
          <>
            <div className="lbar">
              <span className="up">{evalSets.length} eval sets</span>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => setNewModal({ type: "eval" })}
                disabled={corpuses.length === 0}
              >
                <Plus size={14} /> New eval set
              </button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {evalSets.map((ev) => {
                const c = corpuses.find((x) => x.id === ev.corpusId);
                return (
                  <div className="card asset assetrow" key={ev.id}>
                    <div className="logo" style={{ background: "var(--subtle)", color: "var(--fg)" }}>
                      <Target size={14} />
                    </div>
                    <div
                      style={{ flex: 1, minWidth: 0, cursor: "pointer" }}
                      onClick={() => setOpenEval(ev.id)}
                    >
                      <div style={{ fontWeight: 600 }}>{ev.name}</div>
                      <div
                        className="mono tert"
                        style={{
                          fontSize: 11,
                          marginTop: 2,
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                          flexWrap: "wrap",
                        }}
                      >
                        answer key →{" "}
                        {c ? (
                          c.name
                        ) : (
                          <span
                            className="tchip t3"
                            title="This eval set's source corpus was removed — pick a new corpus to score against."
                          >
                            corpus deleted
                          </span>
                        )}{" "}
                        · {Object.keys(ev.expected).length} labeled
                      </div>
                    </div>
                    <div className="assetacts">
                      <button className="btn btn-ghost btn-sm" onClick={() => dupEval(ev)}>
                        <Layers size={13} /> Duplicate
                      </button>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => setOpenEval(ev.id)}
                      >
                        Open <ChevronRight size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
        {tab === "eval" && evalSet && (
          <>
            <div className="crumb">
              <button className="btn btn-ghost btn-sm" onClick={() => setOpenEval(null)}>
                <ChevronLeft size={14} /> Eval sets
              </button>
              <input
                className="inp"
                style={{ flex: 1, maxWidth: 280 }}
                value={evalSet.name}
                onChange={(e) => renameEval(evalSet.id, e.target.value)}
              />
              <span className="tert" style={{ fontSize: 12 }}>
                scores against
              </span>
              <select
                className="sel"
                style={{ width: 200 }}
                value={evalSet.corpusId}
                onChange={(e) => setEvalCorpus(evalSet.id, e.target.value)}
              >
                {corpuses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <span className="spacer" />
              <button className="btn btn-ghost btn-sm" onClick={() => delEval(evalSet.id)}>
                <Trash2 size={14} /> Delete
              </button>
            </div>
            <div className="card" style={{ padding: "12px 14px", marginBottom: 12 }}>
              <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.6 }}>
                This is the <b>answer key</b> for the corpus. For each email, set the{" "}
                <b>expected catalog</b> target (which matter it should land in, or Unassociated)
                and tick any <b>planted issue</b> the spine ought to detect. Attach this set when
                configuring a run; the Simulation's Eval tab then scores actual vs. expected —
                cataloging accuracy and detection recall.
              </div>
            </div>
            <div className="card" style={{ overflow: "auto" }}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Expected catalog</th>
                    <th>Planted flags</th>
                    <th>Expected check outputs (JSON)</th>
                  </tr>
                </thead>
                <tbody>
                  {(evalCorpus ? evalCorpus.emails : []).map((e) => {
                    const ex = evalSet.expected[e.id] || { catalog: ["unassoc"] };
                    return (
                      <tr key={e.id}>
                        <td style={{ maxWidth: 220 }}>
                          <div style={{ fontWeight: 500, fontSize: 12.5 }}>
                            #{e.id} {e.subject}
                          </div>
                        </td>
                        <td>
                          <select
                            className="sel"
                            value={(ex.catalog && ex.catalog[0]) || "unassoc"}
                            onChange={(t) =>
                              setExpected(evalSet.id, e.id, { catalog: [t.target.value] })
                            }
                          >
                            {allWs.map((w) => (
                              <option key={w.id} value={w.id}>
                                {w.name}
                              </option>
                            ))}
                            <option value="unassoc">Unassociated</option>
                          </select>
                        </td>
                        <td>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                            {EVAL_FLAGS.map(([k, lab]) => (
                              <label
                                key={k}
                                style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12 }}
                              >
                                <input
                                  type="checkbox"
                                  className="chk"
                                  style={{ width: 16, height: 16 }}
                                  checked={!!(ex as Record<string, any>)[k]}
                                  onChange={(t) =>
                                    setExpected(evalSet.id, e.id, { [k]: t.target.checked })
                                  }
                                />
                                {lab}
                              </label>
                            ))}
                          </div>
                        </td>
                        <td style={{ minWidth: 230 }}>
                          {(() => {
                            const k = `ck:${evalSet.id}:${e.id}`;
                            return (
                              <textarea
                                className="inp mono"
                                style={{
                                  width: "100%",
                                  minHeight: 52,
                                  fontSize: 11,
                                  resize: "vertical",
                                  ...(jsonBad[k] ? { borderColor: "var(--red)" } : {}),
                                }}
                                placeholder='{"memo":{"facts":[{"field":"…","value":"…"}]}}'
                                title={contractHint}
                                value={
                                  jsonDrafts[k] ??
                                  (ex.checks ? JSON.stringify(ex.checks, null, 1) : "")
                                }
                                onChange={(t) =>
                                  editJson(k, t.target.value, (v) =>
                                    setExpected(evalSet.id, e.id, { checks: v }),
                                  )
                                }
                              />
                            );
                          })()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="card" style={{ padding: "12px 14px", marginTop: 12 }}>
              <div className="up" style={{ marginBottom: 6 }}>
                Expected final tracker state (corpus-level)
              </div>
              <div className="tert" style={{ fontSize: 11.5, marginBottom: 8, lineHeight: 1.5 }}>
                JSON: workstream id → tracker key (timeline, issues, closingItems, signatures,
                diligence, commitments, wires) → entries. Scored as a keyed set match (F1)
                against the live run's accumulated tracker state; divergence from per-email
                check scores is surfaced as pipeline drift in the Eval tab.
              </div>
              {(() => {
                const k = `ft:${evalSet.id}`;
                return (
                  <textarea
                    className="inp mono"
                    style={{
                      width: "100%",
                      minHeight: 90,
                      fontSize: 11,
                      resize: "vertical",
                      ...(jsonBad[k] ? { borderColor: "var(--red)" } : {}),
                    }}
                    placeholder='{"ws_alpha":{"timeline":[{"date":"2026-05-01","event":"…"}],"issues":[{"issue":"…","status":"open"}]}}'
                    value={
                      jsonDrafts[k] ??
                      (evalSet.finalTrackers
                        ? JSON.stringify(evalSet.finalTrackers, null, 2)
                        : "")
                    }
                    onChange={(t) =>
                      editJson(k, t.target.value, (v) =>
                        setFinalTrackers(evalSet.id, v as EvalSet["finalTrackers"]),
                      )
                    }
                  />
                );
              })()}
            </div>
          </>
        )}

        {/* CHECK REGISTRY */}
        {/* AGENT REGISTRY — the workbench: directory -> bundle view -> builder.
            Bundles live in the agent_bundle tables (the source of truth); this
            tab reads and writes them through the API, so what it shows is what
            a pinned version runs. */}
        {tab === "checks" && <Workbench corpuses={corpuses} workstreams={allWs} onBundleOpenChange={setWbOpen} />}

        {tab === "settings" && <SettingsView />}

        {/* WORKSTREAMS */}
        {tab === "ws" &&
          (() => {
            const bundle = wsBundles.find((b) => b.id === openBundle);
            if (!bundle)
              return (
                <div>
                  <div className="lbar">
                    <span className="up">
                      {wsBundles.length} workstream bundle{wsBundles.length === 1 ? "" : "s"}
                    </span>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => setNewModal({ type: "workstream" })}
                    >
                      <Plus size={14} /> New bundle
                    </button>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {wsBundles.map((b) => (
                      <div className="card asset assetrow" key={b.id}>
                        <div
                          className="logo"
                          style={{ background: "var(--subtle)", color: "var(--fg)" }}
                        >
                          <Layers size={14} />
                        </div>
                        <div
                          style={{ flex: 1, minWidth: 0, cursor: "pointer" }}
                          onClick={() => setOpenBundle(b.id)}
                        >
                          <div style={{ fontWeight: 600 }}>{b.name}</div>
                          <div className="mono tert" style={{ fontSize: 11, marginTop: 2 }}>
                            {b.workstreams.length} workstream
                            {b.workstreams.length === 1 ? "" : "s"} · {b.note} · created{" "}
                            {fmtDate(b.createdAt)}
                          </div>
                        </div>
                        <div className="assetacts">
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => dupBundle(b)}
                          >
                            <Copy size={13} /> Duplicate
                          </button>
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => setOpenBundle(b.id)}
                          >
                            Open <ChevronRight size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div
                    className="tert"
                    style={{ fontSize: 11.5, marginTop: 12, lineHeight: 1.5 }}
                  >
                    A bundle is a collection of workstreams (working groups + deal-memo fields)
                    tied to a corpus. Duplicate one to make a variant and tweak variables without
                    touching the original — then select it when configuring a run.
                  </div>
                </div>
              );
            return (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 300px",
                  gap: 14,
                  alignItems: "start",
                }}
              >
                <div>
                  <div className="lbar" style={{ gap: 8 }}>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => setOpenBundle(null)}
                    >
                      <ChevronLeft size={14} /> Bundles
                    </button>
                    <input
                      className="inp"
                      style={{ flex: 1, fontWeight: 600 }}
                      value={bundle.name}
                      onChange={(e) => renameBundle(bundle.id, e.target.value)}
                    />
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => dupBundle(bundle)}
                    >
                      <Copy size={13} /> Duplicate
                    </button>
                    {wsBundles.length > 1 && (
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => delBundle(bundle.id)}
                      >
                        <Trash2 size={13} /> Delete
                      </button>
                    )}
                  </div>
                  <div className="hint" style={{ marginBottom: 12 }}>
                    {bundle.note}
                  </div>
                  <div className="lbar">
                    <span className="up">
                      {bundle.workstreams.length} workstream
                      {bundle.workstreams.length === 1 ? "" : "s"}
                    </span>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => addWorkstream(bundle.id)}
                    >
                      <Plus size={14} /> Add workstream
                    </button>
                  </div>
                  {bundle.workstreams.length === 0 && (
                    <div className="card" style={{ padding: 24, textAlign: "center" }}>
                      <div className="empty" style={{ padding: 0 }}>
                        No workstreams in this bundle yet.
                      </div>
                    </div>
                  )}
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {bundle.workstreams.map((w, idx) => (
                      <WorkstreamCard
                        key={w.id}
                        w={w}
                        bid={bundle.id}
                        idx={idx}
                        h={wsOps}
                        isMobile={isMobile}
                      />
                    ))}
                  </div>
                </div>
                <div>
                  <div className="up" style={{ marginBottom: 8 }}>
                    Change log
                  </div>
                  {(bundle.log || [])
                    .slice()
                    .reverse()
                    .map((l, i) => (
                      <div className="verrow" key={i}>
                        <div>
                          <div style={{ fontWeight: 500, fontSize: 13 }}>{l}</div>
                          <div className="tert" style={{ fontSize: 11 }}>
                            {fmtDate(bundle.updated || bundle.createdAt)}
                          </div>
                        </div>
                      </div>
                    ))}
                  <div
                    className="tert"
                    style={{ fontSize: 11.5, marginTop: 8, lineHeight: 1.5 }}
                  >
                    Working-group membership drives cataloging, privilege reasoning, and
                    new-participant flags. Tweak a duplicated bundle to test variants against the
                    same corpus.
                  </div>
                </div>
              </div>
            );
          })()}

        {editing && (
          <EmailEditor
            st={editing}
            onClose={() => setEditing(null)}
            onSave={saveEmail}
            onDelete={delEmail}
          />
        )}
        {confirmDel && (
          <div className="modal" onClick={() => setConfirmDel(null)}>
            <div
              className="mbox"
              style={{ width: "min(440px,100%)" }}
              role="alertdialog"
              aria-label="Confirm corpus deletion"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mbhd">
                <Trash2 size={15} color="var(--red)" />
                <div className="mt">Delete corpus?</div>
              </div>
              <div style={{ padding: "16px 18px", fontSize: 13, color: "var(--muted)", lineHeight: 1.6 }}>
                <b style={{ color: "var(--fg)" }}>{confirmDel.name}</b> and its{" "}
                {confirmDel.emails.length} email
                {confirmDel.emails.length === 1 ? "" : "s"} will be permanently deleted.
                Eval sets that score against it will need a new corpus. This can't be
                undone.
              </div>
              <div className="mbft" style={{ justifyContent: "flex-end" }}>
                <button className="btn btn-ghost btn-sm" onClick={() => setConfirmDel(null)}>
                  Cancel
                </button>
                <button
                  className="btn btn-sm"
                  style={{ background: "var(--red)", color: "#fff" }}
                  onClick={() => {
                    delCorpus(confirmDel.id);
                    setConfirmDel(null);
                  }}
                >
                  <Trash2 size={13} /> Delete corpus
                </button>
              </div>
            </div>
          </div>
        )}
        {newModal && (
          <NewEntityModal
            type={newModal.type}
            corpuses={corpuses}
            onClose={() => setNewModal(null)}
            onBlank={startBlank}
            onImport={importEntities}
          />
        )}
      </div>
    </main>
  );
}
